import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import clsx from 'clsx';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { IconButton } from 'office-ui-fabric-react';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { DetailsRow } from 'office-ui-fabric-react/lib/DetailsList';
import DetailsList from '../DetailsList';
import Dropdown from '../Dropdown';
import Checkbox from '../Checkbox';
import styles from './BundledProductList.style';

const onRenderCheckForFooterRow = allocationAggregate => (
  <div className={styles.footerRow}>
    <Text className={styles.allocationText}>Total Allocation</Text>
    <TextField
      className={styles.allocation}
      ariaLabel="Total Allocation"
      value={`${allocationAggregate}%`}
      readOnly
    />
  </div>
);

const onRenderDetailsFooter = (showAllocationError, allocationAggregate, isAddBundle) => (
  <>
    <DetailsRow
      item={{}}
      itemIndex={-1}
      onRenderCheck={() => onRenderCheckForFooterRow(allocationAggregate)}
      className={styles.footer}
    />

    {showAllocationError && (
      <Text
        className={
          isAddBundle
            ? clsx(styles.allocationErrorMessage, styles.errorMessageAlignment)
            : styles.allocationErrorMessage
        }
      >
        Total allocation value must equal 100%
      </Text>
    )}
  </>
);

const BundledProductList = ({
  items,
  removable,
  enableView,
  onRemove,
  className: classNameProp,
  disabled,
  hideTokenId,
  setAllocation,
  allProductsSelected,
  handleProductSelect,
  handleUpdatedProductList,
  showAllocationError,
  setShowAllocationError,
  selectedProductAllocations,
  isAddBundle,
  ...other
}) => {
  const className = clsx(classNameProp);
  const [updateItems, setUpdatedItems] = useState([]);
  const [allocationAggregate, setAllocationAggregate] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectAllProducts, setSelectAllProducts] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const allocationSum = el => el.reduce((a, b) => +a + +b.allocationPercentage, 0);

  useEffect(() => {
    setUpdatedItems(items);
    setTotalCount(items.length);
    const aggregateAmount = allocationSum(items);
    setAllocationAggregate(aggregateAmount);
    const showError = (!!updateItems?.length || !!aggregateAmount) && !(aggregateAmount === 100);
    setShowAllocationError(showError, !(aggregateAmount === 100));
  }, [items]);

  useEffect(() => {
    setSelectedProducts(selectedProductAllocations);
  }, [selectedProductAllocations]);

  useEffect(() => {
    setSelectAllProducts(allProductsSelected);
  }, [allProductsSelected]);

  const handleKeyPress = event => {
    const inputValue = event?.target?.value;
    if (
      inputValue?.indexOf('.') !== -1 &&
      event?.target?.selectionStart >= inputValue?.length - 5 &&
      inputValue?.substring(inputValue.indexOf('.')).length > 5
    ) {
      event.preventDefault();
    }
  };

  const handleChange = (tokenId, value) => {
    const updatedValue = value ? value.replace(/[^0-9\\.]/g, '') : '';
    const updatedItems = updateItems.map(i =>
      i.tokenId === tokenId ? { ...i, allocationPercentage: updatedValue } : { ...i },
    );
    handleUpdatedProductList(updatedItems, 'allocationValue');
  };

  const handleProducIdChange = (tokenId, value) => {
    const updatedItems = updateItems.map(i =>
      i.tokenId === tokenId ? { ...i, productId: value } : { ...i },
    );
    setUpdatedItems(updatedItems);
    handleUpdatedProductList(updatedItems);
  };

  const handleRoyaltyBearingChange = (tokenId, value) => {
    const updatedItems = updateItems.map(i =>
      i.tokenId === tokenId ? { ...i, isRoyaltyBearing: value } : { ...i },
    );
    setUpdatedItems(updatedItems);
    handleUpdatedProductList(updatedItems);
  };

  const onHandleColumnClick = () => {
    handleProductSelect([]);
    if (selectAllProducts) {
      setSelectAllProducts(false);
    } else {
      const setItems = updateItems;
      const products = setItems.map(item => item.tokenId);
      handleProductSelect(products);
      setSelectAllProducts(true);
    }
  };

  const onSelectProducts = productTokenId => {
    if (selectedProducts.length > 0 && selectedProducts.includes(productTokenId)) {
      const products = selectedProducts.filter(product => product !== productTokenId);
      // Uncheck the colum header select products if the selected products lengh becomes zero.
      setSelectAllProducts(false);
      handleProductSelect(products);
    } else {
      const products = [...selectedProducts, productTokenId];
      handleProductSelect(products);
      // Check the colum header select products if the selected products lengh becomes zero.
      if (selectedProducts.length === totalCount - 1) {
        setSelectAllProducts(true);
        handleProductSelect(products);
      }
    }
  };

  const selectMenuProps = {
    items: [
      {
        key: 'selectProductSummary',
        text: 'Select To Add Product to bundle',
        iconProps: { iconName: 'LocationCircle' },
      },
    ],
  };

  const columns = [
    {
      key: 'select',
      name: 'select',
      headerClassName: styles.iconColumnHeaderSelectButton,
      arianame: 'icon',
      iconName: selectAllProducts ? 'SkypeCircleCheck' : '',
      isIconOnly: true,
      fieldName: 'select',
      minWidth: 20,
      maxWidth: 20,
      onColumnClick: onHandleColumnClick,
      onRender: item => {
        const productTokenId = get(item, 'tokenId');
        const iconName = selectedProducts.includes(productTokenId) ? 'SkypeCircleCheck' : '';
        return (
          <div>
            <IconButton
              className={styles.selectButton}
              menuProps={selectMenuProps}
              ariaLabel="Select"
              iconProps={{ iconName }}
              menuIconProps={{ iconName: '' }}
              onMenuClick={event => {
                event.preventDefault();
                event.stopPropagation();
                onSelectProducts(productTokenId);
              }}
            />
          </div>
        );
      },
    },
    {
      key: 'icon',
      name: 'icon',
      headerClassName: styles.iconColumnHeader,
      iconClassName: styles.headerIcon,
      arianame: 'icon',
      iconName: 'Tag',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 8,
      maxWidth: 8,
      columnActionsMode: 0,
      onRender: () => {
        return (
          <div className={styles.iconColumn}>
            <FontIcon iconName="Tag" arianame="Tag" />
          </div>
        );
      },
    },
    {
      key: 'name',
      name: 'Product Name',
      fieldName: 'name',
      isResizable: false,
      minWidth: 200,
      maxWidth: 200,
    },
    {
      key: 'tokenID',
      name: 'Token ID',
      fieldName: 'tokenId',
      isResizable: false,
      minWidth: 120,
      maxWidth: 120,
      onRender: item => {
        return (
          <span>
            <b className={styles.bold}>#{item.tokenId}</b>
          </span>
        );
      },
    },
    {
      key: 'productId',
      name: 'Product ID',
      fieldName: 'productId',
      isResizable: false,
      minWidth: 220,
      maxWidth: 220,
      onRender: item => {
        const { tokenId, productId } = item;
        return (
          <Dropdown
            aria-label="ProductIds Dropdown"
            field={{ name: 'productId', value: [productId] }}
            label=""
            options={item.productsIds}
            className={styles.productDropDown}
            onChange={(event, prodItem) => handleProducIdChange(tokenId, prodItem.key)}
            disabled={item?.productsIds?.length === 0}
          />
        );
      },
    },
    {
      key: 'isRoyaltyBearing',
      name: 'Royalty Bearing',
      fieldName: 'isRoyaltyBearing',
      isResizable: false,
      minWidth: 70,
      maxWidth: 70,
      onRender: item => {
        const { tokenId, isRoyaltyBearing } = item;
        return (
          <Checkbox
            ariaLabel="isRoyaltyBearing Checkbox"
            field={{ name: 'royaltyBearing', value: isRoyaltyBearing }}
            label=""
            onChange={event => handleRoyaltyBearingChange(tokenId, event.target.checked)}
          />
        );
      },
    },
    {
      key: 'allocationPercentage',
      name: 'Allocation',
      fieldName: 'allocationPercentage',
      minWidth: 120,
      maxWidth: 120,
      onRender: item => {
        const { tokenId } = item;
        return (
          <span className={styles.allocationCell}>
            <TextField
              name="allocationPercentage"
              ariaLabel="allocation Percentage"
              className={styles.allocationBox}
              value={item.allocationPercentage}
              onKeyPress={handleKeyPress}
              onChange={event => handleChange(tokenId, event.target.value)}
            />
          </span>
        );
      },
    },
    {
      key: 'tasks',
      name: 'Tasks',
      styles: { root: { visibility: 'hidden' } },
      headerClassName: styles.listActions,
      isResizable: true,
      minWidth: 50,
      maxWidth: 50,
      onRender: item => {
        return (
          <IconButton
            iconProps={{ iconName: 'More' }}
            title="Options"
            ariaLabel="Options"
            onRenderMenuIcon={() => null}
            menuProps={{
              shouldFocusOnMount: false,
              items: [
                {
                  key: 'removeProduct',
                  text: 'Remove',
                  onClick: () => onRemove([item.tokenId]),
                },
                {
                  key: 'viewProduct',
                  text: 'View',
                  onClick: () => window.open(item.link),
                },
              ],
            }}
          />
        );
      },
    },
  ];

  return (
    <DetailsList
      items={updateItems.map(product => ({
        ...product,
        key: product.tokenId,
        link: `/products/${product.tokenId}`,
      }))}
      columns={columns}
      className={className}
      onRenderDetailsFooter={() =>
        onRenderDetailsFooter(showAllocationError, allocationAggregate, isAddBundle)
      }
      {...other}
    />
  );
};

BundledProductList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      tokenId: PropTypes.number,
      name: PropTypes.string,
      owner: PropTypes.string,
    }),
  ),
  removable: PropTypes.bool,
  enableView: PropTypes.bool,
  onRemove: PropTypes.func,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  setAllocation: PropTypes.func,
  hideTokenId: PropTypes.bool,
  allProductsSelected: PropTypes.bool,
  handleProductSelect: PropTypes.func,
  handleUpdatedProductList: PropTypes.func,
  showAllocationError: PropTypes.bool,
  setShowAllocationError: PropTypes.func,
  selectedProductAllocations: PropTypes.array,
  isAddBundle: PropTypes.bool,
};

BundledProductList.defaultProps = {
  items: [],
  removable: false,
  enableView: false,
  onRemove: null,
  className: '',
  disabled: false,
  setAllocation: null,
  hideTokenId: false,
  allProductsSelected: false,
  handleProductSelect: null,
  handleUpdatedProductList: null,
  showAllocationError: false,
  setShowAllocationError: () => {},
  selectedProductAllocations: [],
  isAddBundle: false,
};

export default BundledProductList;
